import { useEffect, useEffectEvent } from "react"
import { UseFormReturn, useWatch } from "react-hook-form"
import { useDebounce, useUpdateEffect } from "react-use"
import { addMonths } from "date-fns"

import * as callViewValue from "@/components/CallView/value"
import { config } from "@/config"
import { useFormValidation } from "@/hooks/formValidation"
import { contactStorageService } from "@/services/localStorageService"

import { CallReportOption } from "./call-report-options"

export function useCallReportStatelessForm({
  availableCallReportOptions,
  form,
  isDisabled,
  isSkipMode = false,
  onSave,
}: {
  availableCallReportOptions: CallReportOption[]
  form: UseFormReturn<callViewValue.Decoder.DecoderType>
  isDisabled: boolean
  isSkipMode?: boolean
  onSave: (data: callViewValue.Decoder.DecoderType) => void
}) {
  const { validDate, validateScheduledDateTime } = useFormValidation()

  // persist form state to local storage; text fields are debounced
  const persistToLocalStorage = (isSaved: boolean) => {
    const formState = form.getValues()

    const value = {
      ...formState,
      updated_at: isSaved ? new Date() : undefined,
    }

    contactStorageService.setCallReport({ value, isSaved })
  }

  // Reset when entering or leaving a state that disables the form.
  useUpdateEffect(() => {
    form.reset()
  }, [isDisabled])

  // Leaving won't-call does not change isDisabled. Clear the skip-mode draft
  // so it is not stored or posted on the next call.
  useUpdateEffect(() => {
    if (isSkipMode) {
      return
    }

    form.reset()
  }, [isSkipMode])

  usePersistForm(form, () => persistToLocalStorage(false))

  const { control, handleSubmit, resetField, watch } = form

  const selectedStatus = useWatch({
    control,
    name: "status",
  })

  const selectedReason = useWatch({
    control,
    name: "reason",
  })

  const options = {
    status: availableCallReportOptions.map((item) => item.status),
    reason:
      availableCallReportOptions.find(
        (item) => item.status.value === selectedStatus,
      )?.reasons ?? [],
  }

  const isDatePickerEnabled = shouldScheduleCall(
    selectedReason || selectedStatus,
  )

  const clearDateField = () =>
    resetField("scheduledCallDate", { defaultValue: null })

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      // Reset reason when status changes
      if (name === "status" && type === "change") {
        resetField("reason", { defaultValue: "" })
      }

      // Reset date value when the field is not available
      const shouldClearDateField =
        !shouldScheduleCall(value.reason || value.status) &&
        Boolean(value.scheduledCallDate)
      if (shouldClearDateField) {
        clearDateField()
      }
    })

    return () => subscription.unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitValidForm = handleSubmit((data) => {
    persistToLocalStorage(true)

    onSave(data)
  })

  const dateConstraints = {
    minDate: new Date(),
    maxDate: addMonths(new Date(), config.maxScheduledCallMonths),
  }

  return {
    options,
    clearDateField,
    dateConstraints,
    handleSubmit: handleSubmitValidForm,
    validDate,
    isDatePickerEnabled,
    isDisabled,
    validateScheduledDateTime,
    selectedStatus,
  }
}

function shouldScheduleCall(reason?: string) {
  return reason === "日時を指定して再架電"
}

function usePersistForm(
  form: UseFormReturn<callViewValue.Decoder.DecoderType>,
  persistFormState: (state: callViewValue.Decoder.DecoderType) => void,
) {
  const [status, reason, scheduledCallDate] = form.watch([
    "status",
    "reason",
    "scheduledCallDate",
  ])
  const memoText = form.watch("memo")

  const onFormValuesChange = useEffectEvent(() => {
    persistFormState(form.getValues())
  })

  useEffect(() => {
    onFormValuesChange()
  }, [status, reason, scheduledCallDate])

  useDebounce(
    () => {
      persistFormState(form.getValues())
    },
    500,
    [memoText],
  )
}
